import { createFileRoute, Link } from "@tanstack/react-router";
import { Download, Landmark, ReceiptText, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { billingPlanName } from "@/lib/billing.catalog";
import { getPublicBillingDocument } from "@/lib/billing.functions";
import { downloadBillingCreditNotePdf, downloadBillingInvoicePdf } from "@/lib/billing-pdf";
import { formatBillingPeriod, formatBillingPrice } from "@/lib/billing.types";

export const Route = createFileRoute("/rechnung/$token")({
  staticData: { sitemap: false, toasts: true },
  loader: ({ params }) => getPublicBillingDocument({ data: { token: params.token } }),
  component: PublicInvoicePage,
  head: () => ({
    meta: [
      { title: "Begner · Rechnungsdokument" },
      { name: "robots", content: "noindex, nofollow" },
      { name: "referrer", content: "no-referrer" },
    ],
  }),
});

const statusLabel = {
  draft: "In Vorbereitung",
  open: "Offen",
  paid: "Bezahlt",
  overdue: "Überfällig",
  void: "Storniert",
  refunded: "Gutgeschrieben",
} as const;

function PublicInvoicePage() {
  const { invoice, settings } = Route.useLoaderData();
  const planName = billingPlanName(invoice.planCode, "de");
  const payable =
    invoice.amountMinor > 0 && (invoice.status === "open" || invoice.status === "overdue");
  const period = formatBillingPeriod(invoice.servicePeriodStart, invoice.servicePeriodEnd, "de-CH");

  return (
    <main className="container-page py-12 sm:py-20">
      <div className="mx-auto max-w-3xl space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link to="/" className="font-display text-2xl font-bold text-brand-deep">
            Begner
          </Link>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <ShieldCheck className="size-4 text-emerald-600" /> Sicheres Rechnungsdokument
          </div>
        </div>

        <Card className="overflow-hidden border-border/70 shadow-lg">
          <div className="h-2 bg-gradient-brand" />
          <CardHeader className="flex-row items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-brand-deep">
                Rechnung
              </p>
              <CardTitle className="mt-2 font-display text-3xl">{invoice.invoiceNumber}</CardTitle>
            </div>
            <Badge variant={payable ? "secondary" : "outline"}>
              {invoice.settlementKind === "discount"
                ? "Durch Rabatt ausgeglichen"
                : statusLabel[invoice.status]}
            </Badge>
          </CardHeader>
          <CardContent className="space-y-6">
            <dl className="grid gap-4 rounded-2xl bg-muted/35 p-5 sm:grid-cols-2">
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">Leistung</dt>
                <dd className="mt-1 font-bold">{planName}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">Betrag</dt>
                <dd className="mt-1 font-bold">
                  {formatBillingPrice(invoice.amountMinor, "de-CH", invoice.currency)}
                </dd>
              </div>
              {invoice.discountMinor > 0 ? (
                <div className="sm:col-span-2 text-sm">
                  <p>Ausgangspreis: {formatBillingPrice(invoice.subtotalMinor, "de-CH")}</p>
                  <p>
                    Rabatt {invoice.discountCode ?? "Aktion"}
                    {invoice.discountCode && invoice.discountPercent
                      ? ` (${invoice.discountPercent} %)`
                      : ""}
                    : −{formatBillingPrice(invoice.discountMinor, "de-CH")}
                  </p>
                </div>
              ) : null}
              {period ? (
                <div className="sm:col-span-2">
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                    Leistungszeitraum
                  </dt>
                  <dd className="mt-1 font-medium">{period}</dd>
                </div>
              ) : null}
            </dl>

            {payable ? (
              <div className="rounded-2xl border border-primary/20 bg-brand-soft/45 p-5">
                <p className="flex items-center gap-2 font-bold">
                  <Landmark className="size-4 text-primary" /> Banküberweisung / QR-Rechnung
                </p>
                <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-[100px_minmax(0,1fr)]">
                  <dt className="text-muted-foreground">IBAN</dt>
                  <dd className="break-all font-mono font-semibold">{settings.iban}</dd>
                  <dt className="text-muted-foreground">Referenz</dt>
                  <dd className="break-all font-mono font-semibold">{invoice.paymentReference}</dd>
                  {invoice.dueAt ? (
                    <>
                      <dt className="text-muted-foreground">Zahlbar bis</dt>
                      <dd>{new Intl.DateTimeFormat("de-CH").format(new Date(invoice.dueAt))}</dd>
                    </>
                  ) : null}
                </dl>
              </div>
            ) : null}

            {invoice.status === "void" ? (
              <p className="rounded-xl border border-red-200 bg-red-50 p-4 font-semibold text-red-800">
                Diese Rechnung wurde storniert und darf nicht bezahlt werden.
              </p>
            ) : null}

            <div className="flex flex-wrap gap-3">
              <Button
                type="button"
                onClick={() =>
                  void downloadBillingInvoicePdf(invoice, settings, planName).catch((error) =>
                    toast.error(
                      error instanceof Error ? error.message : "PDF konnte nicht erstellt werden.",
                    ),
                  )
                }
              >
                <ReceiptText className="size-4" /> Rechnung herunterladen
              </Button>
              {invoice.creditNoteNumber ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    void downloadBillingCreditNotePdf(invoice, settings, planName).catch((error) =>
                      toast.error(
                        error instanceof Error
                          ? error.message
                          : "Gutschrift konnte nicht erstellt werden.",
                      ),
                    )
                  }
                >
                  <Download className="size-4" /> Gutschrift herunterladen
                </Button>
              ) : null}
            </div>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
